import { useState } from 'react';
import { Bookmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useFavoriteUsers } from '@/hooks/useFavoriteUsers';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { useLocalizations } from '@/hooks/useLocalizations';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';
import { AuthModal } from '@/components/auth/AuthModal';

interface FavoriteButtonProps {
  userId: string;
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  showText?: boolean;
}

export function FavoriteButton({ 
  userId, 
  variant = 'ghost', 
  size = 'icon',
  className,
  showText = false 
}: FavoriteButtonProps) {
  const { user } = useAuth();
  const { isFavorite, toggleFavorite, isToggling } = useFavoriteUsers();
  const { lCommon } = useLocalizations();
  const { trackFavoriteAdd, trackFavoriteRemove } = useAnalyticsTracking();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      setShowAuthModal(true);
      return;
    }

    // Track favorite action
    if (isFavorite(userId)) {
      trackFavoriteRemove(userId);
    } else {
      trackFavoriteAdd(userId);
    }

    toggleFavorite(userId);
  };

  const isFav = isFavorite(userId);

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={variant}
              size={size}
              onClick={handleClick}
              disabled={isToggling}
              className={cn('group [&_svg]:size-5', className)}
            >
              <Bookmark
                className={cn(
                  'h-5 w-5 transition-all',
                  isFav && 'fill-yellow-400 text-yellow-500',
                  !isFav && 'text-muted-foreground group-hover:fill-yellow-400 group-hover:text-yellow-500'
                )}
              />
              {showText && (
                <span className="ml-2">
                  {isFav ? lCommon('labels.saved', { defaultValue: 'Saved' }) : lCommon('actions.save', { defaultValue: 'Save' })}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isFav
              ? lCommon('labels.remove_from_favorites', { defaultValue: 'Remove from favorites' })
              : lCommon('labels.save_to_favorites', { defaultValue: 'Save to favorites' })}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} variant="favorite" />
    </>
  );
}
